import React, { Ref, useEffect, useImperativeHandle } from 'react';
import {
  Button,
  TextField,
  Typography,
  Divider,
  FormHelperText,
  DialogProps,
  useMediaQuery,
  MenuItem,
} from '@mui/material';
import { useSelector } from 'react-redux';

import { useStylesModalOffer } from './hooks/useStyles';
import { useDefineUserRole, useFormatMessage } from '../../../../../hooks';
import NumberFormatPaddingCustom from '../../../../../components/formComponents/NumberFormatPaddingCustom';
import { thousands } from '../../../../../utils/utils';
import { IAppState } from '../../../../../store/rootDuck';
import { ICompany } from '../../../../../interfaces/companies';
import { useGetCompanies } from './hooks/useGetCompanies';
import { useCreateOffer } from './hooks/useCreateOffer';
import { Modal } from '../../../../../components/other/Modals';
import CompaniesTable from './CompaniesTable';
import { useGetOffers } from './hooks/useGetOffers';
import { useGetProductCompany } from '../../ProductCatalogCompany/CompanyContext';
import { useGetCompany } from './hooks/useGetCompany';

const initPrices = [
  { price: 0, amount: 0 },
  { price: 0, amount: 0 },
  { price: 0, amount: 0 },
  { price: 0, amount: 0 },
  { price: 0, amount: 0 },
];

type QuantityLevel = 'none' | 'little' | 'medium' | 'much';

export type OfferMethods = {
  setCompany: (value: ICompany | null) => void;
} | null;

const initFields = {
  stockQuantity: 0,
  orderQuantity: 0,
  deliveryPeriod: 0,
  secondQuantityLevel: 'none' as QuantityLevel,
  quantityLevel: 'none' as QuantityLevel,
};

export function Offer({ id, offerRef }: { id?: number; offerRef?: Ref<OfferMethods> }) {
  const companyProduct = useGetProductCompany();
  const fm = useFormatMessage();
  const { classes } = useStylesModalOffer();
  const { me } = useSelector((store: IAppState) => ({
    me: store.profile.me,
  }));
  const isManager = useDefineUserRole(me, ['ROLE_MANAGER']);
  const isVendor = useDefineUserRole(me, ['ROLE_VENDOR', 'ROLE_VENDOR_STAFF']);
  const [prices, setPrices] = React.useState(initPrices);
  const [fields, setFields] = React.useState(initFields);
  const [openDialogCompanies, setOpenDialogCompanies] = React.useState(false);
  const { create, loadingCreate, success } = useCreateOffer();
  const { fetchOffers, offers, loadingOffers } = useGetOffers();
  const [company, setCompany] = React.useState<ICompany | null>(null);
  const {
    fetch: fetchCompanies,
    loading: loadingCompanies,
    page,
    companies,
  } = useGetCompanies();
  const isTablet = useMediaQuery('(max-width:920px)');
  const { fetchCompany, loading } = useGetCompany({ setCompany });

  useImperativeHandle(offerRef, () => ({
    setCompany,
  }));

  useEffect(() => {
    if (companyProduct?.companyId) {
      fetchCompany(companyProduct.companyId);
    }
  }, [companyProduct]);

  const handleCreate = () => {
    if (!id || !me) return;
    if ((isVendor && me?.company?.id) || ((me?.is_admin || isManager) && company?.id)) {
      create({
        prices: prices.filter(item => item.price > 0 && item.amount > 0),
        companyId: me?.is_admin || isManager ? company!.id! : me.company!.id!,
        productId: id,
        ...fields,
        quantityLevel: fields.quantityLevel !== 'none' ? fields.quantityLevel : undefined,
        secondQuantityLevel:
          fields.secondQuantityLevel !== 'none' ? fields.secondQuantityLevel : undefined,
      });
    }
  };

  useEffect(() => {
    if (company && id && (me?.is_admin || isManager)) {
      fetchOffers({ company_id: company.id, product_id: id });
    }
  }, [company, id, success]);

  useEffect(() => {
    if (isVendor && id && me?.company?.id) {
      fetchOffers({ company_id: me.company.id, product_id: id });
    }
  }, [isVendor, id, success]);

  useEffect(() => {
    if (offers) {
      setPrices([...(offers[0]?.prices || []), ...initPrices].slice(0, 5));
      setFields({
        stockQuantity: offers[0]?.stock_quantity || 0,
        orderQuantity: offers[0]?.order_quantity || 0,
        deliveryPeriod: offers[0]?.delivery_period || 0,
        quantityLevel: offers[0]?.quantity_level || 'none',
        secondQuantityLevel: offers[0]?.second_quantity_level || 'none',
      });
    }
  }, [offers]);

  const hasValidPrice = prices.some(item => item.price > 0 && item.amount > 0);
  const hasStockOrOrder =
    fields.stockQuantity > 0 || (fields.orderQuantity > 0 && fields.deliveryPeriod > 0);
  const hasQuantityLevel =
    fields.quantityLevel !== 'none' || fields.secondQuantityLevel !== 'none';
  const roleCompanyOk =
    (isVendor && !!me?.company?.id) || ((me?.is_admin || isManager) && !!company?.id);
  const isSaveDisabled =
    !roleCompanyOk ||
    loadingOffers ||
    loadingCreate ||
    loading ||
    !hasValidPrice ||
    (!hasStockOrOrder && !hasQuantityLevel);

  return (
    <>
      <div className={classes.offer}>
        <div className={classes.offerHeader}>
          <div
            className={classes.containerTitle}
            style={{
              flexDirection: isTablet ? 'column' : 'row',
              alignItems: isTablet ? 'flex-start' : 'center',
              width: isTablet ? '100%' : 'auto',
            }}
          >
            <Typography className={classes.titlePrices} component='p'>
              {`${fm('PRODUCT.TABLE.PRICES')}${
                me?.is_admin || isManager ? ` от компании` : ''
              }`}
            </Typography>

            {(me?.is_admin || isManager) && (
              <div className={classes.column} style={{ width: isTablet ? '100%' : 'auto' }}>
                <TextField
                  variant='outlined'
                  placeholder='Компания'
                  size='small'
                  value={company ? company.brand || company.name : ''}
                  disabled={loading}
                  onClick={() => {
                    fetchCompanies(1, 20, 'ROLE_VENDOR');
                    setOpenDialogCompanies(true);
                  }}
                  sx={{
                    '& fieldset': { border: 'none' },
                  }}
                  className={classes.textFieldCompany}
                  InputProps={{
                    className: classes.input,
                    readOnly: true,
                    style: { padding: isTablet ? '0 14px' : 8, height: '100%', fontSize: 16 },
                  }}
                />
                {!isTablet
                  ? (me?.is_admin || isManager) &&
                    !company && <FormHelperText error>Выберите компанию</FormHelperText>
                  : null}
              </div>
            )}
          </div>
          {!isTablet
            ? (isVendor || me?.is_admin || isManager) && (
                <div className={classes.column}>
                  <Button
                    variant='contained'
                    className={classes.save}
                    onClick={handleCreate}
                    disabled={isSaveDisabled}
                  >
                    {fm('COMMON.BUTTON.SAVE')}
                  </Button>
                  {isVendor && !me?.company?.id && (
                    <FormHelperText error>У вас нет компании</FormHelperText>
                  )}
                  {(me?.is_admin || isManager) && !company && (
                    <FormHelperText error>Выберите компанию</FormHelperText>
                  )}
                </div>
              )
            : null}
        </div>
        <div className={classes.containerColumn}>
          <div className={classes.onceColumn}>
            <div className={classes.containerFields}>
              <Typography className={classes.subTitle} component='p'>
                Кол-во (шт)
              </Typography>
              {[...Array(5).keys()].map(i => (
                <div key={i} className={classes.column}>
                  <div className={classes.containerField}>
                    <TextField
                      variant='outlined'
                      placeholder='0'
                      size='small'
                      value={prices[i].amount}
                      onChange={e =>
                        setPrices(state =>
                          state.map((item, index) =>
                            index === i ? { ...item, amount: +e.target.value } : item
                          )
                        )
                      }
                      sx={{
                        '& fieldset': { border: 'none' },
                      }}
                      className={classes.textField}
                      InputProps={{
                        className: classes.input,
                        inputComponent: NumberFormatPaddingCustom as any,
                        inputMode: 'numeric',
                        style: isTablet
                          ? {
                              height: '100%',
                              padding: '0 14px',
                              fontSize: 16,
                            }
                          : {},
                      }}
                    />
                    <Typography className={classes.x} component='p'>
                      x
                    </Typography>
                  </div>
                </div>
              ))}
            </div>
            <div className={classes.containerFields}>
              <Typography className={classes.subTitle} component='p'>
                Цена за 1 шт.
              </Typography>
              {[...Array(5).keys()].map(i => (
                <div key={i} className={classes.column}>
                  <div className={classes.containerField}>
                    <TextField
                      variant='outlined'
                      placeholder='0'
                      size='small'
                      value={prices[i].price}
                      onChange={e =>
                        setPrices(state =>
                          state.map((item, index) =>
                            index === i ? { ...item, price: +e.target.value } : item
                          )
                        )
                      }
                      sx={{
                        '& fieldset': { border: 'none' },
                      }}
                      className={classes.textField}
                      InputProps={{
                        className: classes.input,
                        inputComponent: NumberFormatPaddingCustom as any,
                        inputMode: 'numeric',
                        style: isTablet
                          ? {
                              height: '100%',
                              padding: '0 14px',
                              fontSize: 16,
                            }
                          : {},
                      }}
                    />
                    <Typography className={classes.rub} component='p'>
                      RUB
                    </Typography>
                    {!isTablet ? (
                      <Typography className={classes.summ} component='p'>
                        =
                      </Typography>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
            {!isTablet ? (
              <div className={classes.containerFields}>
                <Typography className={classes.subTitle} component='p'>
                  Сумма за партию
                </Typography>
                {[...Array(5).keys()].map(i => (
                  <div key={i} className={classes.column}>
                    <div className={classes.containerFieldSumm}>
                      <Typography className={classes.price} component='p'>
                        {thousands(String((prices[i].amount * prices[i].price).toFixed(2)))}
                      </Typography>
                      <Typography className={classes.rub} component='p'>
                        RUB
                      </Typography>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
          <Divider
            orientation='vertical'
            flexItem
            sx={{
              borderRightWidth: 3,
              borderColor: '#e6e7e8',
            }}
          />
          <div>
            <Typography className={classes.subTitle} component='p'>
              Наличие на складе (шт)
            </Typography>
            <div className={classes.containerField}>
              <TextField
                variant='outlined'
                placeholder='0'
                size='small'
                value={fields.stockQuantity}
                onChange={e => setFields({ ...fields, stockQuantity: +e.target.value })}
                sx={{
                  '& fieldset': { border: 'none' },
                }}
                className={classes.textField}
                style={{ width: 128, marginRight: 15 }}
                InputProps={{
                  className: classes.input,
                  inputComponent: NumberFormatPaddingCustom as any,
                  inputMode: 'numeric',
                  style: isTablet
                    ? {
                        height: '100%',
                        padding: '0 14px',
                        fontSize: 16,
                      }
                    : {},
                }}
              />
              <TextField
                select
                variant='outlined'
                size='small'
                value={fields.quantityLevel}
                onChange={e => {
                  setFields({ ...fields, quantityLevel: e.target.value as QuantityLevel });
                }}
                sx={{
                  '& fieldset': { border: 'none' },
                  '& .MuiSelect-select': {
                    padding: 0,
                  },
                }}
                className={classes.textField}
                style={{ width: 138 }}
                InputProps={{
                  className: classes.input,
                  style: isTablet
                    ? {
                        height: '100%',
                        padding: '0 14px',
                        fontSize: 16,
                      }
                    : {},
                }}
              >
                <MenuItem value='none'>-</MenuItem>
                <MenuItem value='much'>много</MenuItem>
                <MenuItem value='medium'>средне</MenuItem>
                <MenuItem value='little'>мало</MenuItem>
              </TextField>
            </div>
            <Typography className={classes.subTitle} marginTop='20px' component='p'>
              Количество под заказ (шт) / срок поставки (дн)
            </Typography>
            <div className={classes.containerField}>
              <TextField
                variant='outlined'
                placeholder='0'
                size='small'
                value={fields.orderQuantity}
                onChange={e => setFields({ ...fields, orderQuantity: +e.target.value })}
                sx={{
                  '& fieldset': { border: 'none' },
                }}
                // style={{ width: !isTablet ? '100%' : 128 }}
                style={{ width: 128, marginRight: 15 }}
                className={classes.textField}
                InputProps={{
                  className: classes.input,
                  inputComponent: NumberFormatPaddingCustom as any,
                  inputMode: 'numeric',
                  style: isTablet
                    ? {
                        height: '100%',
                        padding: '0 14px',
                        fontSize: 16,
                      }
                    : {},
                }}
              />
              <TextField
                select
                variant='outlined'
                size='small'
                value={fields.secondQuantityLevel}
                onChange={e => {
                  setFields({
                    ...fields,
                    secondQuantityLevel: e.target.value as QuantityLevel,
                  });
                }}
                sx={{
                  '& fieldset': { border: 'none' },
                  '& .MuiSelect-select': {
                    padding: 0,
                  },
                }}
                className={classes.textField}
                style={{ width: 138 }}
                InputProps={{
                  className: classes.input,
                  style: isTablet
                    ? {
                        height: '100%',
                        padding: '0 14px',
                        fontSize: 16,
                      }
                    : {},
                }}
              >
                <MenuItem value='none'>-</MenuItem>
                <MenuItem value='much'>много</MenuItem>
                <MenuItem value='medium'>средне</MenuItem>
                <MenuItem value='little'>мало</MenuItem>
              </TextField>
              <Typography className={classes.x} component='p'>
                /
              </Typography>
              <TextField
                variant='outlined'
                placeholder='0'
                size='small'
                value={fields.deliveryPeriod}
                onChange={e => setFields({ ...fields, deliveryPeriod: +e.target.value })}
                sx={{
                  '& fieldset': { border: 'none' },
                }}
                className={classes.textFieldWeek}
                InputProps={{
                  className: classes.input,
                  inputComponent: NumberFormatPaddingCustom as any,
                  inputMode: 'numeric',
                  style: isTablet
                    ? {
                        height: '100%',
                        padding: '0 14px',
                        fontSize: 16,
                      }
                    : {},
                }}
              />
            </div>
          </div>
          {isTablet
            ? (isVendor || me?.is_admin || isManager) && (
                <div className={classes.column}>
                  <Button
                    variant='contained'
                    className={classes.save}
                    style={{ width: '100%' }}
                    onClick={handleCreate}
                    disabled={isSaveDisabled}
                  >
                    {fm('COMMON.BUTTON.SAVE')}
                  </Button>
                  {isVendor && !me?.company?.id && (
                    <FormHelperText error>У вас нет компании</FormHelperText>
                  )}
                  {(me?.is_admin || isManager) && !company && (
                    <FormHelperText error>Выберите компанию</FormHelperText>
                  )}
                </div>
              )
            : null}
        </div>
      </div>

      <Modal
        DialogProps={{ maxWidth: 'md' } as DialogProps}
        open={openDialogCompanies}
        onClose={loadingCreate ? () => {} : () => setOpenDialogCompanies(false)}
        title={fm('COMPANY.EDIT.MOUNT_TITLE')}
        loading={loadingCreate || loadingCompanies}
        content={
          <CompaniesTable
            page={page.page}
            perPage={page.perPage}
            total={page.total}
            onConnect={company => {
              setCompany(company);
              setOpenDialogCompanies(false);
            }}
            connectedCompanies={company ? [company.id] : []}
            companies={companies || []}
            fetch={({ page, perPage }) => fetchCompanies(page, perPage, 'ROLE_VENDOR')}
          />
        }
        actions={[
          {
            title: fm('COMMON.BUTTON.CLOSE'),
            onClick: () => setOpenDialogCompanies(false),
          },
        ]}
      />
    </>
  );
}
